/**
 * A list about one record — a property's jobs, a contact's invoices.
 *
 * It is the list engine, not a smaller thing that resembles it. Same table,
 * same 30px header, same sorting and dragging and resizing and pinning, same
 * mark on the left of a job.
 *
 * Two shapes, and the difference is how much room it has:
 *
 * **`card`** — four rows on the Overview, under a header with a count and
 * "View all". A peek. The settings panels roll into one button because a card
 * in a rail has no room for a toolbar.
 *
 * **`tab`** — a whole tab to itself, and therefore the whole thing: the search
 * box, the filter, import, export, the settings menu, the saved views and a
 * New button. The truncated version was built before these were tabs, when
 * they were cards stacked down a profile page; in a tab it is just a list
 * missing its controls, and somebody looking at forty of a property's visits
 * wants to search them exactly as much as they would on the Visits screen.
 *
 * The record it is about is a `baseFilter`, which never reaches the view: it
 * is not a filter somebody chose, it is what the list *is*, and a chip
 * offering to remove it would offer to turn a property's jobs into every job
 * in the company.
 */
import { useNavigate } from 'react-router-dom'
import type { FilterCondition } from '@rowted/shared'
import { ListScreen } from './list-screen.js'
import { RowTray } from './row-tray.js'
import type { MenuItem } from './context-menu.js'
import { CellStack } from './data-list.js'
import { invoiceMark, jobMark } from './landing-marks.js'
import { VISIT_CELLS, VISIT_PRIMARY, VISIT_ROW_FIELDS, visitRowMark } from './visit-rows.js'
import { Button, StatusChip, humanize } from './primitives.js'
import { IconPlus } from '../frame/icons.js'
import { ViewAllLink, viewAllHref } from './record-list.js'
import { useList, type ListArea, type ListRow } from '../lib/use-list.js'
import type { request } from '../lib/api.js'
import type { ReactNode } from 'react'

export interface RelatedListProps {
  area: ListArea
  title: string
  /** What makes it this record's list. Never shown as a filter chip. */
  baseFilter: FilterCondition[]
  /**
   * What these settings are filed under when somebody keeps them.
   *
   * Per *kind* of related list, not per record: "the jobs on a property" is
   * one list that people want to look the same on every property, which is
   * the whole point of keeping the settings for everyone.
   */
  listKey: string
  /** The columns this list opens with, before anybody changes them. */
  fields?: string[]
  emptyTitle: string
  emptyBody: string
  /** Where "View all" goes. Defaults to the area, filtered the same way. */
  viewAllFilter?: FilterCondition[]
  /** `card` is a peek on the Overview; `tab` is the whole list engine. */
  variant?: 'card' | 'tab'
  /**
   * Which client asks for the rows. The tenant one unless told otherwise.
   *
   * Rowted Admin's records hang the same lists off themselves — a client's
   * People, an admin's Sessions — and every URL those build has to land under
   * `/api/v1/platform`. `adminRequest` here is what makes that one prop
   * rather than a second copy of this component.
   */
  request?: typeof request
  /** The New button, on a `tab`. */
  onNew?: { label: string; onClick: () => void }
  /**
   * The ⋮ on each row — the same menu a module's own list hangs off its rows.
   *
   * A related list had none, so a client's People tab could list the owner
   * and do nothing about them (Aaron, step 27: "I have no actions regarding
   * the primary user or any user").
   */
  rowMenu?: (row: ListRow) => MenuItem[]
}

export function RelatedList({
  area, title, baseFilter, listKey, fields, emptyTitle, emptyBody, viewAllFilter,
  variant = 'card', onNew, request: send, rowMenu,
}: RelatedListProps) {
  const navigate = useNavigate()
  const preset = PRESETS[area] ?? PRESETS.jobs!
  const tab = variant === 'tab'
  const list = useList(area, {
    storage: 'local',
    listKey,
    baseFilter,
    ...(send ? { request: send } : {}),
    /*
     * A tab has room for the ordinary row height; a card is showing four rows
     * in a rail and needs them tight.
     */
    defaultView: { fields, density: tab ? 'default' : 'compact' },
    alwaysFetch: preset.always,
  })

  return (
    <ListScreen
      area={area}
      chrome={tab ? 'embedded' : 'related'}
      title={title}
      list={list}
      primaryColumn={preset.primary}
      {...(preset.mark ? { mark: { render: preset.mark } } : {})}
      cell={preset.cell}
      {...(preset.hasTray
        ? {
          tray: (row: ListRow) => (preset.hasTray!(row)
            ? (
              <RowTray
                area={area}
                id={String(row.id)}
                {...(send ? { request: send } : {})}
              />
            )
            : null),
        }
        : {})}
      {...(rowMenu ? { rowMenu } : {})}
      onRowClick={(row) => {
        const href = preset.rowHref ? preset.rowHref(row) : `${preset.href}/${String(row.id)}`
        if (href) navigate(href)
      }}
      headerLink={tab
        ? undefined
        : <ViewAllLink href={viewAllHref(area, viewAllFilter ?? baseFilter)} />}
      actions={tab && onNew
        ? (
          <Button variant="primary" onClick={onNew.onClick} className="h-8 gap-1.5">
            <IconPlus size={14} />
            {onNew.label}
          </Button>
        )
        : undefined}
      emptyTitle={emptyTitle}
      emptyBody={emptyBody}
    />
  )
}

interface Preset {
  primary: {
    field: string
    header: string
    width?: number
    render: (row: ListRow) => { primary: ReactNode; secondary?: ReactNode }
  }
  /** The same mark the module's own list draws, at the same 28px. */
  mark?: (row: ListRow) => ReactNode
  /**
   * Whether this row has anything to expand.
   *
   * Only whether — the tray itself is one `<RowTray>` built below with the
   * area, the id and the same `request` the rows came from, so a list that
   * lives under `/api/v1/platform` does not need its own copy of it. A preset
   * without this has no chevron on any row.
   */
  hasTray?: (row: ListRow) => boolean
  /** Columns that are more than formatted text — a status chip, mostly. */
  cell?: Record<string, (row: ListRow) => ReactNode>
  href: string
  /**
   * Where a row opens, when it is not `href/id`.
   *
   * Returning null leaves the row unclickable, which is better than a link
   * to a screen that does not exist.
   */
  rowHref?: (row: ListRow) => string | null
  /**
   * Fields the preset reads whether or not they are columns.
   *
   * The bold first column's second line, the row's mark and where a row
   * opens all come out of the row, and a caller choosing its own `fields`
   * has no reason to know that — so a property's jobs list asking for
   * title, status and expected silently lost the job number under the title
   * until these were named here.
   */
  always?: string[]
}

/*
 * The bold first column and the mark, per area.
 *
 * Only the two things `ListScreen` cannot work out from the field catalogue:
 * which column is the record's name, and which mark its rows wear. Every
 * other column comes from the area's own definition, so a column added to the
 * Jobs board is a column this list can show too.
 */
const PRESETS: Partial<Record<ListArea, Preset>> = {
  jobs: {
    primary: {
      field: 'title',
      header: 'Job',
      width: 190,
      render: (row) => ({
        primary: String(row.title ?? ''),
        secondary: [row.reference, row.propertyName].filter(Boolean).join(' · ') || undefined,
      }),
    },
    /* The same tile the Jobs board draws, from the same function — a job's
       row on a property's Jobs tab and on the board are the same job. */
    mark: jobMark,
    cell: {
      status: (row) => (
        <StatusChip group="job" status={String(row.status ?? '')}>
          {humanize(String(row.status ?? ''))}
        </StatusChip>
      ),
    },
    href: '/jobs',
    always: ['status', 'reference', 'propertyName',
      'serviceLine', 'divisionIcon', 'divisionColour', 'jobType'],
  },
  invoices: {
    primary: {
      field: 'reference',
      header: 'Invoice',
      width: 160,
      render: (row) => ({
        primary: String(row.reference ?? ''),
        secondary: row.customerName ? String(row.customerName) : undefined,
      }),
    },
    mark: invoiceMark,
    cell: {
      status: (row) => (
        <StatusChip group="invoice" status={String(row.status ?? '')}>
          {humanize(String(row.status ?? ''))}
        </StatusChip>
      ),
    },
    href: '/invoices',
    always: ['status', 'reference', 'customerName', 'amount', 'paid'],
  },
  visits: {
    /*
     * **One row per visit** (Aaron, Oct 30) — the Visits list's own row, from
     * the same `visit-rows.tsx`: the name over *V-#### · 26 of 30*, the
     * calendar card with its second card and loop, the visit's §17.1 status,
     * folder 32's Date · Days · Crew · Est h · Actual · Invoice cells. A job's,
     * a property's and a contact's Visits tab and the Visits screen are one
     * row drawn one way.
     */
    primary: VISIT_PRIMARY,
    mark: (row) => visitRowMark(row),
    cell: VISIT_CELLS,
    /* A row is a visit, and opens it — `/visits/<work item id>`, never a day. */
    href: '/visits',
    always: VISIT_ROW_FIELDS,
  },
  properties: {
    primary: {
      field: 'name',
      header: 'Property',
      width: 190,
      render: (row) => ({
        primary: String(row.name ?? ''),
        secondary: row.addressLine1 ? String(row.addressLine1) : undefined,
      }),
    },
    href: '/properties',
    always: ['name', 'addressLine1'],
  },

  /*
   * Rowted Admin's own (`rowted-admin-v3.md` §2.3, §3.3–3.5).
   *
   * `href: ''` on three of them, and a `rowHref` that returns null: a row of
   * activity and a signed-in device have no page to open. The engine leaves
   * such a row unclickable rather than linking to a screen that does not
   * exist — the tray is where the detail is.
   */
  'admin-activity': {
    primary: {
      field: 'title',
      header: 'What',
      width: 260,
      render: (row) => ({
        primary: String(row.title ?? ''),
        secondary: [row.reason, row.tenantName].filter(Boolean).join(' · ') || undefined,
      }),
    },
    href: '',
    rowHref: () => null,
    always: ['title', 'reason', 'tenantName', 'kind', 'impersonationId'],
    /*
     * A visit into a customer, and nothing else. `adminActivityTray` answers
     * two panels for one of those — what was changed inside, and the terms the
     * visit was made on — and has nothing to say about a sign-in.
     */
    hasTray: (row) => Boolean(row.impersonationId),
  },
  'admin-sessions': {
    primary: {
      field: 'device',
      header: 'Device',
      width: 240,
      render: (row) => ({
        primary: String(row.device ?? 'Unknown device'),
        secondary: [row.place, row.insideClient && `inside ${String(row.insideClient)}`]
          .filter(Boolean).join(' · ') || undefined,
      }),
    },
    href: '',
    rowHref: () => null,
    always: ['device', 'place', 'status', 'insideClient'],
  },
  'client-people': {
    primary: {
      field: 'name',
      header: 'Person',
      width: 210,
      render: (row) => ({
        primary: String(row.name ?? ''),
        /*
         * The address alone. The role used to be here too, and it is a column
         * three along — §7's rule is that a line repeating the cell beside it
         * is a line that says nothing.
         */
        secondary: String(row.email ?? '') || undefined,
      }),
    },
    href: '',
    rowHref: () => null,
    always: ['name', 'email', 'roles', 'status', 'passwordSet'],
  },
  'client-activity': {
    primary: {
      field: 'title',
      header: 'What',
      width: 260,
      /*
       * The reason alone. The admin's name used to lead it and is the column
       * two along — §7: a line repeating the cell beside it says nothing.
       */
      render: (row) => ({
        primary: String(row.title ?? ''),
        secondary: String(row.reason ?? '') || undefined,
      }),
    },
    href: '',
    rowHref: () => null,
    always: ['title', 'who', 'reason', 'kind', 'impersonationId'],
    /* Same rule as the admin's Activity: only a visit has anything inside. */
    hasTray: (row) => Boolean(row.impersonationId),
  },
  'client-sessions': {
    primary: {
      field: 'who',
      header: 'Admin',
      width: 240,
      /*
       * The name alone. Reason and Access used to trail it and are both
       * columns two along — §7: a line repeating the cell beside it is a line
       * that says nothing. What would earn the space is *who they went in as*,
       * and that name is a `users` row inside the customer, so putting it here
       * would make the list cross a tenant boundary for a subtitle. The tray
       * shows it, where the crossing is already made and recorded.
       */
      render: (row) => ({ primary: String(row.who ?? 'Somebody at Rowted') }),
    },
    href: '',
    rowHref: () => null,
    always: ['who', 'reason', 'access'],
    /*
     * Every row *is* a visit here, so every row expands — unlike Activity,
     * where a sign-in has nothing inside it. The tray's id is the row's own.
     */
    hasTray: () => true,
  },
}

export { CellStack }
